import React, { createContext, useContext, useState, useEffect } from "react";

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider"
    );
  }
  return context;
};

// Translation files
const translations = {
  en: {
    // Common
    question: "Question",
    outOf: "out of",
    years: "years",
    next: "Next",
    continue: "Continue",
    previous: "Previous",
    complete: "Complete",
    submitting: "Submitting...",
    male: "Male",
    female: "Female",
    welcome: "Welcome",
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    back: "Back",
    submit: "Submit",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    loadingAssessments: "Loading assessments...",
    loggedInAs: "Logged in as",
    home: "Home",
    logout: "Logout",
    login: "Login",

    // Dashboard
    dashboard: {
      sessionConnecting: "Session will begin shortly",
      sessionConnectingSubText: "You’ve been successfully logged in , please hold on",
      welcomeMessage: "Welcome, {name}!",
      checkVitals: "Vitals",
      joinConsultation: "Consultation",
      healthAssessment: "Assessments",
      askAI: "Ask AI",
      knowledgeBank: "PHM Knowledge Bank",
      endSession: "End session",
      sessionInstruction:
        "Welcome, {name}, during this session you can measure your vitals, complete assessments, or speak with the AI assistant.",
      startSpeaking: "Start speaking",
      listening: "Stop listening",
      connectingToAgent: "Connecting to AI assistant...",
    },

    instructions: {
      downloadOn: "Download on the",
      appStore: "App Store",
      getItOn: "GET IT ON",
      googlePlay: "Google Play",
      back: "Back",
      mobile: "Mobile",
      phone: "Phone",
      step1Title: "1. Scan the QR code",
      step1Description: "Scan the QR displayed on Pod home screen.",
      step1Note: " ",
      step2Title: "2. Download the App",
      step2Description1: "If the app is not installed, you will be prompted to download the Balsamee Continuous Care app after scanning the QR code.",
      step2Description2: "Scan the QR code again after installing the app.",
      step3Title: "3. Open App and Log in",
      step3Description: "Login if you already have an account or create a new one.",
      step4Title: "4. View your results",
      step4Description: "Results will be available inside app after session.",
      step4Note: "Your pod session in the pod has started.",
      downloadOnThe: "Download on the",
      appStore: "App Store",
      getItOn: "GET IT ON",
      googlePlay: "Google Play",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "Welcome to the future of health",
      // subtitle: "Please use your phone camera to scan the code to start the session",
      subtitle: "Please login to start the session",
      getStarted: "Start Your Journey",
      viewInstructions: "View instructions",
      downloadApp: "Download App from here",
      qrExpired: "QR code has expired",
      refetch: "Get new QR code",
      continueAsGuest: "Continue as Guest",
    },

    // Guest Mode
    guestMode: {
      title: "Continue as Guest",
      subtitle: "Enter a few details to start your session",
      firstName: "First Name",
      firstNamePlaceholder: "Enter first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Enter last name",
      username: "Username",
      regenerateUsername: "Generate a new username",
      gender: "Gender",
      submit: "Start Session",
      error: "Something went wrong. Please try again.",
    },

    // App Redirect (open app or store)
    appRedirect: {
      openingApp: "Opening app...",
      redirectingToStore: "Redirecting to store...",
      openManually: "If the app didn't open, tap to open the store",
    },

    // Vitals Check
    vitalsCheck: {
      sessionInstruction: "You’ll be measuring a few vitals using four different devices.",
      title: "Vitals Check",
      checkYourVitals: "Check Your Vitals",
      results: "Results",
      instructions: "Please follow the instructions to check your vitals",
      startCheck: "Start Vitals Check",
      proceedToAssessments: "Proceed to assessments",
      ecgTitle: "ECG",
      ecgProcessing: "Still processing the image",
      viewEcg: "View",
      visualMediaEmpty: "No files available yet",
      vitals: {
        muscleMass: "Muscle Mass",
        weight: "Weight",
        boneMass: "Bone Mass",
        heartPulse: "Heart Pulse",
        fatPercentage: "Fat Percentage",
        bloodPressure: "Blood Pressure",
        bmi: "BMI",
        metabolicAge: "Metabolic Age",
        nerveHealth: "Nerve Health",
        fatRatio: "Fat Ratio",
        hydration: "Hydration",
        height: "Height",
        heartRate: "Heart Rate",
        spo2: "SPO2",
        temperature: "Temperature",
      },
    },

    // Consultation
    consultation: {
      title: "Online Consultation",
      joinCall: "Join Call",
      waitingRoom: "Waiting Room",
      preparing: "Preparing your consultation...",
      connectionError:
        "Unable to connect. Please try again or contact support.",
      retry: "Retry",
      endMeeting: "End Meeting",
      endingMeeting: "Ending meeting...",
      notAvailable: "Consultation Not Available",
      notAvailableMessage: "Online consultation is not available at this time. Please check the schedule and try again during available hours.",
      checkingAvailability: "Checking availability...",
      liveSession: "Live session",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "Call Error",
      joiningCall: "Joining call...",
      retry: "Retry",
      errorOccurred: "An error occurred during the call",
      failedToJoin: "Failed to join the call",
      connectingToDoctor:
        "We're connecting you to your doctor — your consultation will begin shortly.",
      sessionWithDr: "Pod session meeting",
      enableMic: "Please enable microphone access in your browser settings.",
      enableCamera: "Please enable camera access in your browser settings.",
      enablePermissions: "Please grant camera and microphone permissions.",
    },

    // Assessment
    assessment: {
      title: "Health Risk Assessment",
      description: "Your answers help your doctor tailor your consultation",
      skipAll: "Skip all",
      talkToDoctor: "Talk to a doctor",
      questions: "questions",
      assessments: "Assessments",
      groups: {
        mentalHealth: "Psychological and Mental health",
        medicalRisk: "Health Screening & Medical Risk",
        wellbeing: "Well-being & Lifestyle",
        orgPsychology: "Work & Organizational Psychology",
      },
      loadError: "Failed to load assessments. Please try again.",
      errorNoConnection: "No internet connection. Please check your network and try again.",
      errorConnection: "Unable to connect to the server. Please check your internet connection and try again.",
      errorSubmit: "Failed to submit assessment. Please try again.",
      completed: "Completed",
      getStarted: "Get started",
    },

    // End Session
    endSession: {
      title: "You've successfully completed your pod session",
      description: "You can access your measurements report in the app.",
      endButton: "End Session",
      confirmEnd: "Are you sure you want to end this session?",
      confirmTitle: "Are you sure you want to end this session?",
      confirmDescription: "This will end your current session and log you out of the pod.",
      confirmButton: "Yes, I'm sure",
      ending: "Ending session...",
      sessionSummary: "Session Summary",
    },

    // Post-session PDF report (PodSessionReport WS event)
    sessionReport: {
      pendingTitle: "Preparing your report",
      pendingDescription:
        "We're generating the PDF for the session you just finished. This only takes a moment.",
      readyTitle: "Your report is ready",
      previewUnavailable:
        "The preview couldn't be loaded here. Use the button below to open your report.",
      download: "Download report",
      downloading: "Downloading...",
      print: "Print report",
      unavailableTitle: "No report for this session",
      unavailableDescription:
        "No measurements were recorded during this session, so there's nothing to report.",
      failedTitle: "We couldn't prepare your report",
      failedDescription:
        "Something went wrong while generating the PDF. You can still access your measurements in the app.",
    },

    // AI Assistant
    aiAssistant: {
      title: "AI Assistant",
      whatCanIHelp: '"What can I help you with?"',
      instruction:
        "Scan the QR code and we'll connect you with one of our case managers",
      repeatInstructions: "Repeat Instructions",
      skip: "Skip",
      starting: "Starting...",
      start: "Start",
      clickAgainToStop: "Click again to stop",
      talkToAI: "Ask AI",
      chooseLanguage: "Choose AI Assistant Language:",
      chooseGender: "Choose Gender:",
      listening: "I'm listening...",
    },

    // Live Avatar
    liveAvatar: {
      chat: "Chat",
      speak: "Speak",
      start: "Start",
      stop: "Stop",
      listening: "Stop Listening",
      connectingToAvatar: "Connecting to avatar...",
      connected: "Connected",
      disconnected: "Disconnected",
      typeMessage: "Type your message...",
      send: "Send",
      retry: "Retry",
      close: "Close",
      failedToConnect: "Failed to connect to avatar. Please try again.",
      selectAvatarLanguage: "Select Avatar Language",
      avatarWillSpeak: "The avatar will speak in:",
      micPermissionMessage: "Please enable microphone access in your browser settings",
    },

    // Language Toggle
    language: {
      english: "English",
      arabic: "Arabic",
      selectLanguage: "Select Language",
    },

    // Settings
    settings: {
      title: "Settings",
      activeSession: "Active session",
      aiAssistant: "AI assistant",
      language: "Language",
    },

    errors: {
      podAlreadyInUse: "This pod is currently unavailable.",
      usernameTaken: "This username is already taken. Please try another one.",
    },

    // Measure Aspects (backend keys)
    measureAspects: {
      GLUCOSE: "Glucose",
      BLOOD_PRESSURE: "Blood Pressure",
      WEIGHT: "Weight",
      HEIGHT: "Height",
      TEMPERATURE: "Temperature",
      BLOOD_OXYGEN: "Blood Oxygen",
      HEART_RATE: "Heart Rate",
      ECG: "ECG",
      BMI: "BMI",
      BODY_FAT: "Body Fat",
      MUSCLE_MASS: "Muscle Mass",
      BONE_MASS: "Bone Mass",
      VISCERAL_FAT: "Visceral Fat",
      BASAL_METABOLIC_RATE: "Basal Metabolic Rate",
      BODY_WATER: "Body Water",
      PROTEIN: "Protein",
      FAT_FREE_MASS: "Fat-Free Mass",
      SKELETAL_MUSCLE: "Skeletal Muscle",
      BODY_FAT_MASS: "Body Fat Mass",
      MINERAL: "Mineral",
      STEP_COUNT: "Step Count",
      CALORIES: "Calories",
      BONE_SALT: "Bone Salt",
      PROTEIN_RATE: "Protein Rate",
      BODY_FAT_RATE: "Body Fat Rate",
      BODY_WATER_RATE: "Body Water Rate",
      BASAL_METABOLISM: "Basal Metabolism",
      BODY_FAT_CONTENT: "Body Fat Content",
      BODY_MUSCLE_RATE: "Body Muscle Rate",
      BODY_WATER_CONTENT: "Body Water Content",
      URIC_ACID: "Uric Acid",
      VISCERAL_FAT_GRADE: "Visceral Fat Grade",
      BODY_MUSCLE_CONTENT: "Body Muscle Content",
      EXTRACELLULAR_FLUID_RATE: "Extracellular Fluid Rate",
      INTRACELLULAR_FLUID_RATE: "Intracellular Fluid Rate",
      EXTRACELLULAR_FLUID_VOLUME: "Extracellular Fluid Volume",
      INTRACELLULAR_FLUID_VOLUME: "Intracellular Fluid Volume",
      SYSTOLIC: "Systolic",
      DIASTOLIC: "Diastolic",
      SPO2: "SpO2",
      ACTIVE_ENERGY: "Active Energy",
      RESTING_ENERGY: "Resting Energy",
      VISUAL_ACUITY_LEFT: "Visual Acuity (Left)",
      VISUAL_ACUITY_RIGHT: "Visual Acuity (Right)",
      COLOR_BLINDNESS: "Color Blindness",
      HEART_AUSCULTATION_EXAM: "Heart Auscultation",
      LUNGS_AUSCULTATION_EXAM: "Lungs Auscultation",
      COUGH_EXAM: "Cough Exam",
      ABDOMINAL_AUSCULTATION_EXAM: "Abdominal Auscultation",
      SKIN_EXAM: "Skin Exam",
      THROAT_EXAM: "Throat Exam",
      RIGHT_EAR_EXAM: "Right Ear Exam",
      LEFT_EAR_EXAM: "Left Ear Exam",
      FEV1: "FEV1",
      FVC: "FVC",
      PEF: "Peak Expiratory Flow (PEF)",
      LUNG_FUNCTION_INDEX: "Lung Function Index",
      WAIST_CIRCUMFERENCE: "Waist Circumference",
      HIP_CIRCUMFERENCE: "Hip Circumference",
      WAIST_HIP_RATIO: "Waist-Hip Ratio",
      TOTAL_CHOLESTEROL: "Total Cholesterol",
      HDL_CHOLESTEROL: "HDL Cholesterol",
      LDL_CHOLESTEROL: "LDL Cholesterol",
      TRIGLYCERIDES: "Triglycerides",
      CHOLESTEROL_HDL_RATIO: "Cholesterol/HDL Ratio",
    },
    measureResults: {
      Normal: "Normal",
    },

    browserUnsupported: {
      title: "Browser Not Supported",
      subtitle: "This application requires the Web Speech API to function.",
      description: "Please open this page in Google Chrome or Microsoft Edge on a desktop or laptop computer.",
      suggestion: "Recommended browsers",
    },
  },

  ar: {
    // Common
    question: "سؤال",
    outOf: "من",
    years: "سنة",
    complete: "إنهاء",
    submitting: "جاري التسليم...",
    male: "ذكر",
    female: "أنثى",
    welcome: "مرحباً",
    loading: "جاري التحميل...",
    error: "خطأ",
    retry: "إعادة المحاولة",
    back: "رجوع",
    next: "التالي",
    continue: "استكمال",
    previous: "السابق",
    submit: "إرسال",
    cancel: "إلغاء",
    confirm: "تأكيد",
    close: "إغلاق",
    loadingAssessments: "جاري تحميل التقييمات...",
    loggedInAs: "مسجل الدخول باسم",
    home: "الرئيسية",
    logout: "تسجيل الخروج",
    login: "تسجيل الدخول",

    // Dashboard
    dashboard: {
      sessionConnecting: "جارى بدأ الجلسة",
      sessionConnectingSubText: "لقد تم تسجيل الدخول بنجاح، يرجى الإنتظار",
      welcomeMessage: "مرحباً، {name}!",
      checkVitals: "قياسات",
      joinConsultation: "استشارة",
      healthAssessment: "تقييمات",
      askAI: "اسأل المساعد",
      knowledgeBank: "بنك المعرفة الصحي",
      endSession: "إنهاء الجلسة",
      sessionInstruction:
        "مرحباً {name}، خلال هذه الجلسة يمكنك قياس العلامات الحيوية أو إكمال التقييمات أو التحدث مع المساعد الذكي.",
      startSpeaking: "ابدأ التحدث",
      listening: "إيقاف التحدث",
      connectingToAgent: "جاري الاتصال بالمساعد الذكي...",
    },

    instructions: {
      downloadOn: "التنزيل على",
      appStore: "App Store",
      getItOn: "احصل عليه على",
      googlePlay: "Google Play",
      back: "رجوع",
      mobile: "الهاتف",
      phone: "الهاتف",
      step1Title: "١. امسح الرمز",
      step1Description: "امسح الرمز المعروض على الشاشة الرئيسية للبود.",
      step1Note: " ",
      step2Title: "٢. تنزيل التطبيق",
      step2Description1: "إذا لم يكن التطبيق مثبتاً، سيُطلب منك تنزيل تطبيق بلسمي للرعاية المستمرة بعد مسح الرمز.",
      step2Description2: "امسح الرمز مرة أخرى بعد تثبيت التطبيق.",
      step3Title: "٣. افتح التطبيق وسجل الدخول",
      step3Description: "قم بتسجيل الدخول إذا كان لديك حساب بالفعل أو قم بإنشاء حساب جديد.",
      step4Title: "٤. عرض النتائج الخاصة بك",
      step4Description: "ستكون النتائج متاحة داخل التطبيق بعد الجلسة.",
      step4Note: "لقد بدأت جلستك في البود بالفعل.",
      downloadOnThe: "تنزيل من",
      appStore: "App Store",
      getItOn: "متوفر على",
      googlePlay: "Google Play",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "مرحباً بك في مستقبل الصحة",
      // subtitle: "يرجى استخدام كاميرا هاتفك لمسح الرمز لبدء الجلسة",
      subtitle: "يرجى تسجيل الدخول لبدء الجلسة",
      getStarted: "ابدأ رحلتك",
      viewInstructions: "عرض التعليمات",
      downloadApp: "تحميل التطبيق من هنا",
      qrExpired: "انتهت صلاحية الرمز",
      refetch: "الحصول على رمز جديد",
      continueAsGuest: "المتابعة كضيف",
    },

    // Guest Mode
    guestMode: {
      title: "المتابعة كضيف",
      subtitle: "أدخل بعض البيانات لبدء جلستك",
      firstName: "الاسم الأول",
      firstNamePlaceholder: "أدخل الاسم الأول",
      lastName: "اسم العائلة",
      lastNamePlaceholder: "أدخل اسم العائلة",
      username: "اسم المستخدم",
      regenerateUsername: "إنشاء اسم مستخدم جديد",
      gender: "الجنس",
      submit: "بدء الجلسة",
      error: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    },

    // App Redirect (open app or store)
    appRedirect: {
      openingApp: "جاري فتح التطبيق...",
      redirectingToStore: "جاري التوجيه إلى المتجر...",
      openManually: "إذا لم يفتح التطبيق، اضغط لفتح المتجر",
    },

    // Vitals Check
    vitalsCheck: {
      sessionInstruction: "ستقوم ببعض القياسات باستخدام أربع أجهزة مختلفة",
      title: "فحص العلامات الحيوية",
      checkYourVitals: "تحقق من العلامات الحيوية",
      results: "النتائج",
      instructions: "يرجى اتباع التعليمات لفحص العلامات الحيوية",
      startCheck: "بدء فحص العلامات الحيوية",
      proceedToAssessments: "المتابعة إلى التقييمات",
      ecgTitle: "تخطيط القلب",
      ecgProcessing: "جاري معالجة الصورة",
      visualMediaEmpty: "لا توجد ملفات متاحة بعد",
      viewEcg: "عرض",
      vitals: {
        muscleMass: "كتلة العضلات",
        weight: "الوزن",
        boneMass: "كتلة العظام",
        heartPulse: "نبض القلب",
        fatPercentage: "نسبة الدهون",
        bloodPressure: "ضغط الدم",
        bmi: "مؤشر كتلة الجسم",
        metabolicAge: "العمر الأيضي",
        nerveHealth: "صحة الأعصاب",
        fatRatio: "نسبة الدهون",
        hydration: "الترطيب",
        height: "الطول",
        heartRate: "معدل ضربات القلب",
        spo2: "SPO2",
        temperature: "درجة الحرارة",
      },
    },

    // Consultation
    consultation: {
      title: "الاستشارة عبر الإنترنت",
      joinCall: "انضم للمكالمة",
      waitingRoom: "غرفة الانتظار",
      preparing: "جاري تحضير استشارتك...",
      connectionError:
        "غير قادر على الاتصال. يرجى المحاولة مرة أخرى أو الاتصال بالدعم.",
      retry: "إعادة المحاولة",
      endMeeting: "إنهاء المكالمة",
      endingMeeting: "جاري إنهاء المكالمة...",
      notAvailable: "الاستشارة غير متاحة",
      notAvailableMessage: "الاستشارة عبر الإنترنت غير متاحة في هذا الوقت. يرجى التحقق من الجدول الزمني والمحاولة مرة أخرى خلال ساعات العمل المتاحة.",
      checkingAvailability: "جاري التحقق من التوفر...",
      liveSession: "جلسة مباشرة",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "خطأ في المكالمة",
      joiningCall: "جاري الانضمام للمكالمة...",
      retry: "إعادة المحاولة",
      errorOccurred: "حدث خطأ أثناء المكالمة",
      failedToJoin: "فشل في الانضمام للمكالمة",
      connectingToDoctor: "نحن نوصلك بطبيبك — ستبدأ استشارتك قريباً.",
      sessionWithDr: "جلسة بود",
      enableMic: "يرجى تمكين الوصول إلى الميكروفون في إعدادات المتصفح.",
      enableCamera: "يرجى تمكين الوصول إلى الكاميرا في إعدادات المتصفح.",
      enablePermissions: "يرجى منح أذونات الكاميرا والميكروفون.",
    },

    // Assessment
    assessment: {
      title: "تقييم المخاطر الصحية",
      description: "تساعد إجاباتك على تخصيص استشارتك للطبيب",
      skipAll: "تخطي الكل",
      talkToDoctor: "تحدث مع الطبيب",
      questions: "أسئلة",
      assessments: "تقييمات",
      groups: {
        mentalHealth: "علم النفس والصحة النفسية",
        medicalRisk: "الفحص الصحي والمخاطر الطبية",
        wellbeing: "الرفاهية وأسلوب الحياة",
        orgPsychology: "علم نفس العمل والمؤسسات",
      },
      loadError: "فشل تحميل التقييمات. يرجى المحاولة مرة أخرى.",
      errorNoConnection: "لا يوجد اتصال بالإنترنت. يرجى التحقق من الشبكة والمحاولة مرة أخرى.",
      errorConnection: "تعذر الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.",
      errorSubmit: "فشل إرسال التقييم. يرجى المحاولة مرة أخرى.",
      completed: "مكتمل",
      getStarted: "ابدأ الآن",
    },

    // End Session
    endSession: {
      title: "لقد أكملت جلسة البود بنجاح",
      description: "يمكنك الوصول إلى تقرير قياساتك في التطبيق.",
      endButton: "إنهاء الجلسة",
      confirmEnd: "هل أنت متأكد من إنهاء هذه الجلسة؟",
      confirmTitle: "هل أنت متأكد من إنهاء هذه الجلسة؟",
      confirmDescription: "سيتم إنهاء جلستك الحالية وتسجيل خروجك من البود.",
      confirmButton: "نعم، أنا متأكد",
      ending: "جاري إنهاء الجلسة...",
      sessionSummary: "ملخص الجلسة",
    },

    // Post-session PDF report (PodSessionReport WS event)
    sessionReport: {
      pendingTitle: "جاري تجهيز تقريرك",
      pendingDescription:
        "نقوم بإنشاء ملف PDF للجلسة التي أنهيتها للتو. لن يستغرق ذلك سوى لحظات.",
      readyTitle: "تقريرك جاهز",
      previewUnavailable:
        "تعذر عرض المعاينة هنا. استخدم الزر بالأسفل لفتح تقريرك.",
      download: "تحميل التقرير",
      downloading: "جاري التحميل...",
      print: "طباعة التقرير",
      unavailableTitle: "لا يوجد تقرير لهذه الجلسة",
      unavailableDescription:
        "لم يتم تسجيل أي قياسات خلال هذه الجلسة، لذا لا يوجد تقرير.",
      failedTitle: "تعذر تجهيز تقريرك",
      failedDescription:
        "حدث خطأ أثناء إنشاء ملف PDF. لا يزال بإمكانك الوصول إلى قياساتك في التطبيق.",
    },

    // AI Assistant
    aiAssistant: {
      whatCanIHelp: "كيف يمكنني مساعدتك؟",
      title: "المساعد الذكي",
      instruction:
        "امسح الرمز وسنقوم بتوصيلك بأحد مديري الحالة لدينا",
      repeatInstructions: "تكرار التعليمات",
      skip: "تخطي",
      starting: "جاري البدء...",
      start: "ابدأ",
      clickAgainToStop: "انقر مرة أخرى للإيقاف",
      talkToAI: "اسأل المساعد",
      chooseLanguage: "اختر لغة المساعد:",
      chooseGender: "اختر الجنس:",
      listening: "إيقاف التحدث",
    },

    // Live Avatar
    liveAvatar: {
      chat: "محادثة",
      speak: "تحدث",
      start: "ابدأ",
      stop: "توقف",
      listening: "إيقاف التحدث",
      connectingToAvatar: "جاري الاتصال بالمساعد...",
      connected: "متصل",
      disconnected: "غير متصل",
      typeMessage: "اكتب رسالتك...",
      send: "إرسال",
      retry: "إعادة المحاولة",
      close: "إغلاق",
      failedToConnect: "فشل الاتصال بالمساعد. يرجى المحاولة مرة أخرى.",
      selectAvatarLanguage: "اختر لغة المساعد",
      avatarWillSpeak: "سيتحدث المساعد باللغة:",
      micPermissionMessage: "يرجى تمكين الوصول إلى الميكروفون في إعدادات المتصفح",
    },

    // Language Toggle
    language: {
      english: "الإنجليزية",
      arabic: "العربية",
      selectLanguage: "اختر اللغة",
    },

    // Settings
    settings: {
      title: "الإعدادات",
      activeSession: "جلسة نشطة",
      aiAssistant: "المساعد الذكي",
      language: "اللغة",
    },

    errors: {
      podAlreadyInUse: "هذا الجهاز غير متاح حالياً.",
      usernameTaken: "اسم المستخدم هذا مستخدم بالفعل. يرجى تجربة اسم آخر.",
    },

    // Measure Aspects (backend keys)
    measureAspects: {
      GLUCOSE: "الجلوكوز",
      BLOOD_PRESSURE: "ضغط الدم",
      WEIGHT: "الوزن",
      HEIGHT: "الطول",
      TEMPERATURE: "درجة الحرارة",
      BLOOD_OXYGEN: "أكسجين الدم",
      HEART_RATE: "معدل ضربات القلب",
      ECG: "تخطيط القلب",
      BMI: "مؤشر كتلة الجسم",
      BODY_FAT: "دهون الجسم",
      MUSCLE_MASS: "كتلة العضلات",
      BONE_MASS: "كتلة العظام",
      VISCERAL_FAT: "الدهون الحشوية",
      BASAL_METABOLIC_RATE: "معدل الأيض الأساسي",
      BODY_WATER: "ماء الجسم",
      PROTEIN: "البروتين",
      FAT_FREE_MASS: "الكتلة الخالية من الدهون",
      SKELETAL_MUSCLE: "العضلات الهيكلية",
      BODY_FAT_MASS: "كتلة دهون الجسم",
      MINERAL: "المعادن",
      STEP_COUNT: "عدد الخطوات",
      CALORIES: "السعرات الحرارية",
      BONE_SALT: "ملح العظام",
      PROTEIN_RATE: "نسبة البروتين",
      BODY_FAT_RATE: "نسبة دهون الجسم",
      BODY_WATER_RATE: "نسبة ماء الجسم",
      BASAL_METABOLISM: "الأيض الأساسي",
      BODY_FAT_CONTENT: "محتوى دهون الجسم",
      BODY_MUSCLE_RATE: "نسبة عضلات الجسم",
      BODY_WATER_CONTENT: "محتوى ماء الجسم",
      URIC_ACID: "حمض اليوريك",
      VISCERAL_FAT_GRADE: "درجة الدهون الحشوية",
      BODY_MUSCLE_CONTENT: "محتوى عضلات الجسم",
      EXTRACELLULAR_FLUID_RATE: "نسبة السائل خارج الخلية",
      INTRACELLULAR_FLUID_RATE: "نسبة السائل داخل الخلية",
      EXTRACELLULAR_FLUID_VOLUME: "حجم السائل خارج الخلية",
      INTRACELLULAR_FLUID_VOLUME: "حجم السائل داخل الخلية",
      SYSTOLIC: "ضغط الدم الانقباضى",
      DIASTOLIC: "ضغط الدم الانبساطى",
      SPO2: "تشبع الأكسجين",
      ACTIVE_ENERGY: "الطاقة النشطة",
      RESTING_ENERGY: "طاقة الراحة",
      VISUAL_ACUITY_LEFT: "حدة الإبصار (اليسرى)",
      VISUAL_ACUITY_RIGHT: "حدة الإبصار (اليمنى)",
      COLOR_BLINDNESS: "عمى الألوان",
      HEART_AUSCULTATION_EXAM: "تسمع القلب",
      LUNGS_AUSCULTATION_EXAM: "تسمع الرئتين",
      COUGH_EXAM: "فحص السعال",
      ABDOMINAL_AUSCULTATION_EXAM: "تسمع البطن",
      SKIN_EXAM: "فحص الجلد",
      THROAT_EXAM: "فحص الحلق",
      RIGHT_EAR_EXAM: "فحص الأذن اليمنى",
      LEFT_EAR_EXAM: "فحص الأذن اليسرى",
      FEV1: "الحجم الزفيري القسري في الثانية الأولى (FEV1)",
      FVC: "السعة الحيوية القسرية (FVC)",
      PEF: "أقصى معدل زفير (PEF)",
      LUNG_FUNCTION_INDEX: "مؤشر وظائف الرئة",
      WAIST_CIRCUMFERENCE: "محيط الخصر",
      HIP_CIRCUMFERENCE: "محيط الورك",
      WAIST_HIP_RATIO: "نسبة الخصر إلى الورك",
      TOTAL_CHOLESTEROL: "الكوليسترول الكلي",
      HDL_CHOLESTEROL: "الكوليسترول الجيد (HDL)",
      LDL_CHOLESTEROL: "الكوليسترول الضار (LDL)",
      TRIGLYCERIDES: "الدهون الثلاثية",
      CHOLESTEROL_HDL_RATIO: "نسبة الكوليسترول إلى HDL",
    },
    measureResults: {
      Normal: "طبيعي",
    },

    browserUnsupported: {
      title: "المتصفح غير مدعوم",
      subtitle: "يتطلب هذا التطبيق واجهة برمجة التعرف على الكلام للعمل.",
      description: "يرجى فتح هذه الصفحة في متصفح Google Chrome أو Microsoft Edge على جهاز كمبيوتر مكتبي أو محمول.",
      suggestion: "المتصفحات الموصى بها",
    },
  },
};

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    return localStorage.getItem("app-language") || "ar";
  });

  const [isRTL, setIsRTL] = useState(language === "ar");

  // Update RTL state when language changes
  useEffect(() => {
    setIsRTL(language === "ar");
    localStorage.setItem("app-language", language);

    // Update document direction
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split(".");
    let translation = translations[language];

    for (const k of keys) {
      translation = translation?.[k];
    }

    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    // Replace parameters in translation
    return translation.replace(
      /\{(\w+)\}/g,
      (match, param) => params[param] || match
    );
  };

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === "en" ? "ar" : "en";
      localStorage.setItem("app-language", next);
      return next;
    });
  };

  const handleSetLanguage = (lang) => {
    localStorage.setItem("app-language", lang);
    setLanguage(lang);
  };

  const value = {
    language,
    isRTL,
    t,
    toggleLanguage,
    setLanguage: handleSetLanguage,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
